import React, { FC, useContext } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../../index";
import { toast } from "react-toastify";
import SVG from "../../svg/Svg";
import CustomToastContainer from "../notification/CustomToastContainer";
import ConfirmToast from "../notification/Confirm";
import {
  IModules,
  ILines,
  IStation,
  IDeviceSecretIssued,
  IDeviceSecretRevoked,
  IDeviceSecretStatus,
} from "../../../models/IUser";
import { PopupModuleHeader } from "./PopupModuleHeader";
import { PopupModuleInfo, getFieldLabel } from "./PopupModuleInfo";
import { PopupStationsTable } from "./PopupStationsTable";
import { PopupChildModulesTable } from "./PopupChildModulesTable";
import { PopupModuleActions } from "./PopupModuleActions";
import { PopupModuleDeviceSecret } from "./PopupModuleDeviceSecret";
import { PopupModuleApPassword } from "./PopupModuleApPassword";
import { useModuleForm } from "./hooks/useModuleForm";
import { useModuleUpdate } from "./hooks/useModuleUpdate";
import { useModuleRestart } from "./hooks/useModuleRestart";
import { useModuleDeviceSecret } from "./hooks/useModuleDeviceSecret";
import { logApiError } from "../../../utils/logApiError";
import "../popstyle_module.css";

interface PopupModuleProps {
  module: IModules | null;
  lines: ILines[];
  stations: IStation[];
  modules: IModules[];
  handleCloseModal: () => void;
  updateModule?: (
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  updateChildModules?: (
    parentModule: Partial<IModules>,
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  updateSmbModules?: (
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  deleteModule?: (id: number) => Promise<void>;
  restartModule?: (identifier: string) => Promise<any>;
  // DEV-78 (ПРВ-06, ПРВ-05): секрет изделия — только для root, значение
  // приходит один раз в ответе выдачи.
  fetchDeviceSecretStatus?: (moduleId: number) => Promise<IDeviceSecretStatus>;
  issueDeviceSecret?: (moduleId: number) => Promise<IDeviceSecretIssued>;
  revokeDeviceSecret?: (moduleId: number) => Promise<IDeviceSecretRevoked>;
  // `Б2.2` / `Б4`: пароль точки — чтение по нажатию, смена командой, внесение
  // из паспорта. Всё только root; значение в ответах списков не приходит.
  fetchApPassword?: (
    moduleId: number,
  ) => Promise<{ apPassword: string | null; state: string; changedAt: string | null }>;
  rotateApPassword?: (
    moduleId: number,
  ) => Promise<{ state: string; changedAt: string | null }>;
  importApPassword?: (
    moduleId: number,
    apPassword: string,
  ) => Promise<{ state: string; changedAt: string | null }>;
  refreshData?: () => void;
}

const PopupModule: FC<PopupModuleProps> = ({
  module,
  lines,
  stations,
  modules,
  handleCloseModal,
  updateModule,
  updateChildModules,
  updateSmbModules,
  deleteModule,
  restartModule: restartModuleProp,
  fetchDeviceSecretStatus,
  issueDeviceSecret,
  revokeDeviceSecret,
  fetchApPassword,
  rotateApPassword,
  importApPassword,
  refreshData,
}) => {
  const { store } = useContext(Context);

  const limit = module
    ? lines.find((line) => line.id === module.line_id)?.current_limit_line || ""
    : "";

  const {
    editedModule,
    isMutated,
    config,
    changedModuleFields,
    fieldErrors,
    validateModuleFields,
    stationsState,
    childModulesState,
    handleModuleChange,
    handleStationChange,
    handleChildModuleChange,
    setModuleField,
    addChildSlot,
    resetAfterSave,
    changedModuleFieldKeys,
    modifiedStations,
    modifiedChildModules,
  } = useModuleForm(module, stations, modules);

  const { handleResponse, handleError } = useModuleUpdate();
  const { restart } = useModuleRestart(restartModuleProp, refreshData);

  // DEV-78: секрет изделия — только root. Хук вызывается всегда (правило хуков),
  // но при `enabled: false` ни читает признак, ни зовёт операции.
  const isRoot = store.user.role === "root";
  const {
    status: deviceSecretStatus,
    issuedSecret,
    isLoading: isDeviceSecretLoading,
    handleIssue,
    handleRevoke,
    hideIssuedSecret,
  } = useModuleDeviceSecret({
    moduleId: module?.id,
    enabled: isRoot,
    // `п. 1`: перевыпуск подтверждается вводом идентификатора изделия.
    chargeId: module?.charge_id,
    fetchStatus: fetchDeviceSecretStatus,
    issue: issueDeviceSecret,
    revoke: revokeDeviceSecret,
  });

  async function sendUpdate(params: {
    apiFn?: (...args: unknown[]) => Promise<any>;
    apiName: string;
    withChargeId?: boolean;
    extraArgs: unknown[];
  }): Promise<boolean> {
    try {
      if (!params.apiFn) {
        toast.error(`Функция ${params.apiName} не передана`);
        return false;
      }
      if (!editedModule) return false;

      const changedFields = changedModuleFieldKeys.reduce(
        (acc, key) => {
          if (key in editedModule) {
            acc[key] = editedModule[key as keyof IModules];
          }
          return acc;
        },
        {} as Partial<Record<keyof IModules, unknown>>,
      );

      const module: Partial<IModules> = {
        id: editedModule.id,
        ...(params.withChargeId !== false && {
          charge_id: editedModule.charge_id,
        }),
        ...changedFields,
      };

      const response = await params.apiFn(module, ...params.extraArgs);
      return handleResponse(response);
    } catch (error) {
      handleError(error);
      return false;
    }
  }

  async function sendDeleteModule(id: number) {
    try {
      const toastId = `delete-module-${id}`;
      toast(
        <ConfirmToast
          message={`Удалить модуль - ${module?.charge_id} ?`}
          onConfirm={async () => {
            if (deleteModule) {
              await deleteModule(id);
            } else {
              toast.error("Функция deleteModule не передана");
            }
            if (refreshData) refreshData();
            handleCloseModal();
          }}
          type="del"
          title="удалить"
          toastId={toastId}
        />,
        {
          toastId,
          style: {
            borderRadius: 0,
          },
          progressClassName: "toast-progress-delete",
          autoClose: 5000,
        },
      );
    } catch (error) {
      logApiError("Ошибка при удалении модуля:", error);
    }
  }

  const saveChanges = async () => {
    const validationErrors = validateModuleFields();
    if (Object.keys(validationErrors).length > 0) {
      const problems = Object.entries(validationErrors)
        .map(([key, msg]) => `${getFieldLabel(key)} — ${msg}`)
        .join("; ");
      toast.error(`Исправьте ошибки: ${problems}`);
      return;
    }

    let success = false;

    switch (config.saveMode) {
      case "smb":
        success = await sendUpdate({
          apiFn: updateSmbModules,
          apiName: "updateSmbModules",
          extraArgs: [modifiedStations, changedModuleFieldKeys],
        });
        break;
      case "childModules":
        success = await sendUpdate(
          updateChildModules
            ? {
                apiFn: updateChildModules,
                apiName: "updateChildModules",
                withChargeId: false,
                extraArgs: [modifiedChildModules, changedModuleFieldKeys],
              }
            : {
                apiFn: updateModule,
                apiName: "updateModule",
                extraArgs: [
                  modifiedStations,
                  modifiedChildModules,
                  changedModuleFieldKeys,
                ],
              },
        );
        break;
      default:
        success = await sendUpdate({
          apiFn: updateModule,
          apiName: "updateModule",
          extraArgs: [
            modifiedStations,
            modifiedChildModules,
            changedModuleFieldKeys,
          ],
        });
    }

    if (success) {
      resetAfterSave();
    }
  };

  if (!module?.id) {
    return null;
  }

  return (
    <>
      <div className="popup_container">
        <PopupModuleHeader
          module={module}
          onRestart={restart}
          onClose={handleCloseModal}
        />

        <PopupModuleInfo
          module={module}
          editedModule={editedModule}
          modules={modules}
          lines={lines}
          limit={limit}
          changedModuleFields={changedModuleFields}
          fieldErrors={fieldErrors}
          onChange={handleModuleChange}
          onLineChange={(lineId) => setModuleField("line_id", lineId)}
          config={config}
          isRoot={isRoot}
        />

        <div className="popup_module_stations">
          <div>
            <span>
              {config.tableMode === "childModules"
                ? "ДОЧЕРНИЕ МОДУЛИ"
                : "СТАНЦИИ МОДУЛЯ"}
            </span>
            <span style={{ marginLeft: "8px" }}>{SVG["broken_line"]}</span>
          </div>
        </div>

        {config.tableMode === "childModules" ? (
          <PopupChildModulesTable
            data={childModulesState}
            onCellChange={handleChildModuleChange}
            onAdd={addChildSlot}
          />
        ) : (
          <PopupStationsTable
            data={stationsState}
            onCellChange={handleStationChange}
          />
        )}

        <PopupModuleDeviceSecret
          isRoot={isRoot}
          status={deviceSecretStatus}
          isLoading={isDeviceSecretLoading}
          issuedSecret={issuedSecret}
          onIssue={handleIssue}
          onRevoke={handleRevoke}
          onHideIssued={hideIssuedSecret}
        />

        <PopupModuleApPassword
          isRoot={isRoot}
          onFetch={async () => {
            if (!fetchApPassword) throw new Error("чтение пароля недоступно");

            return fetchApPassword(module.id);
          }}
          onRotate={async () => {
            if (!rotateApPassword) throw new Error("смена пароля недоступна");

            const result = await rotateApPassword(module.id);

            if (result.state === "pending") {
              toast.info("пароль отправлен изделию и ждёт подтверждения");
            }
            if (result.state === "confirmed") {
              toast.success("пароль точки подтверждён изделием");
            }

            return result;
          }}
          onImport={async (apPassword) => {
            if (!importApPassword) throw new Error("внесение пароля недоступно");

            const result = await importApPassword(module.id, apPassword);
            toast.success("пароль точки внесён из паспорта");

            return result;
          }}
        />

        <PopupModuleActions
          isMutated={isMutated}
          canDelete={store.user.role === "root"}
          onSave={saveChanges}
          onDelete={() => sendDeleteModule(module.id)}
        />
      </div>
      <CustomToastContainer />
    </>
  );
};

export default observer(PopupModule);
